export const BRAND = {
  primary: "#4B9B2B",
  accent: "#6BC839",
  dark: "#14261A",
  surface: "#F4F2E9",
  sidebar: "#16321D",
} as const;

// Real EspoCRM stages (custom enum definito su Opportunity)
export const STAGES = [
  "Bozza",
  "In lavorazione",
  "Inserita",
  "In attivazione",
  "Accettata",
  "Attivata",
  "Scaduta",
  "Annullata",
] as const;

// Stage "positivi" in ordine del flusso (per lo Stepper)
export const STAGES_POSITIVE = [
  "Bozza",
  "In lavorazione",
  "Inserita",
  "In attivazione",
  "Accettata",
  "Attivata",
] as const;

// Stage terminali alternativi (fuori dal flusso normale)
export const STAGES_TERMINAL_ALT = ["Scaduta", "Annullata"] as const;

export const STAGE_COLORS: Record<string, { bg: string; text: string }> = {
  "Bozza":          { bg: "bg-gray-100",    text: "text-gray-800" },
  "In lavorazione": { bg: "bg-blue-100",    text: "text-blue-800" },
  "Inserita":       { bg: "bg-indigo-100",  text: "text-indigo-800" },
  "In attivazione": { bg: "bg-amber-100",   text: "text-amber-800" },
  "Accettata":      { bg: "bg-teal-100",    text: "text-teal-800" },
  "Attivata":       { bg: "bg-green-100",   text: "text-green-800" },
  "Scaduta":        { bg: "bg-orange-100",  text: "text-orange-800" },
  "Annullata":      { bg: "bg-red-100",     text: "text-red-800" },
};

// Etichetta utente per cTipoPratica. Il valore enum CRM resta invariato
// ("Subentro"): qui mappiamo solo il testo mostrato.
const TIPO_PRATICA_LABEL: Record<string, string> = {
  Subentro: "Subentro",
  "Nuova attivazione": "Nuova attivazione / primo allaccio",
};

export function tipoPraticaLabel(tipo?: string | null): string {
  if (!tipo) return "";
  return TIPO_PRATICA_LABEL[tipo] ?? tipo;
}

// Badge "Business": non solo Società — anche una ditta individuale o un'associazione è un
// cliente business (Opportunity.cTipoSoggetto: Persona | Ditta individuale | Società |
// Associazione). Per il portale l'associazione è business; è il motore A2A a tradurla nel
// suo tipo dedicato (backend/app/a2a_engine/constants.py).
export function isBusiness(tipoSoggetto?: string | null): boolean {
  return tipoSoggetto === "Società" || tipoSoggetto === "Ditta individuale" || tipoSoggetto === "Associazione";
}

const _norm = (s: string) =>
  s.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();

/** Nome cliente da mostrare SOTTO il titolo pratica, o null se il titolo già lo contiene.
 *
 * Il nome pratica generato include il cliente ("Voltura Luce - Palocci Christian"), quindi
 * ripeterlo in una colonna a parte è ridondante. Non lo è invece per una Società, dove il
 * nome mostrato è la ragione sociale ("SETTEUNOTTO SRL") che nel titolo non c'è.
 * Match per parole e non per sottostringa: l'ordine nome/cognome non è costante
 * ("- Pino Maurizio" vs "Maurizio Pino"). Parole di 1-2 lettere ignorate (iniziali, "a", "di"). */
export function clienteExtra(titoloPratica: string, clienteName?: string | null): string | null {
  if (!clienteName) return null;
  const parole = _norm(clienteName).split(" ").filter((p) => p.length > 2);
  if (!parole.length) return null;
  const titolo = ` ${_norm(titoloPratica)} `;
  return parole.every((p) => titolo.includes(` ${p} `)) ? null : clienteName;
}

/** Fornitura della pratica: dal campo CRM `cTipoFornitura` se c'è, altrimenti indovinata
 * dal nome. Il campo è popolato dal plugin sulle nuove pratiche e dal backfill sulle
 * storiche; il fallback copre le pratiche senza bolletta collegata, dove il CRM non ha
 * da dove derivarlo. */
export function fornituraPratica(tipo?: string | null, nomePratica = ""): "Luce" | "Gas" | "Dual" | null {
  if (tipo === "Luce" || tipo === "Gas" || tipo === "Dual") return tipo;
  const n = nomePratica.toLowerCase();
  if (n.includes("luce") && n.includes("gas")) return "Dual";
  if (n.includes("luce")) return "Luce";
  if (n.includes("gas")) return "Gas";
  return null;
}

export const TIPO_BADGE: Record<string, { label: string; bg: string; text: string }> = {
  "Luce": { label: "Luce", bg: "bg-yellow-100", text: "text-yellow-800" },
  "Gas":  { label: "Gas",  bg: "bg-orange-100", text: "text-orange-800" },
  "Dual": { label: "Dual", bg: "bg-purple-100", text: "text-purple-800" },
};
